import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { generatePath } from 'react-router'
import { ReactSVG } from 'react-svg'

import { BlogPost } from '@entities/Blog'
import { ParsedContent, TextMaterialBlock } from '@entities/TextMaterialBlock'

import { URL_BLOG, URL_BLOG_CATEGORY, URL_BLOG_TAG } from '@shared/contants'
import { cn } from '@shared/lib/cn'
import Button from '@shared/ui/button'
import Chip from '@shared/ui/chip'
import WatchCount from '@shared/ui/watch-count'
import { formatDate } from '@shared/uttils/formatDate'
import { parseJson } from '@shared/uttils/parseJson'

import Contents from './Contents'

import styles from './styles.module.scss'

interface IBlogDetailProps {
  data: BlogPost
}

export default function BlogDetail (props: IBlogDetailProps) {
  const { data } = props
  const { t: tb } = useTranslation('blog')

  const ContentsItems = useMemo(() => {
    const parsed = parseJson<ParsedContent>(data?.content)

    return (
      parsed?.blocks
        .filter((block) => block.type === 'header' && block.data.level === 2)
        .map((block) => ({
          id: block.id,
          text: block.data.text,
        })) ?? []
    )
  }, [ data?.content ])

  const handleShare = () => {
    if (navigator.share && data?.title && data?.lead) {
      navigator.share({
        title: data?.title,
        text: data?.lead,
        url: window.location.href,
      })
    }
  }

  const categoryUrl = data.category ? generatePath(URL_BLOG_CATEGORY, { categorySlug: data?.category?.slug }) : URL_BLOG

  const hasContents = ContentsItems && ContentsItems.length > 0

  return (
    <div className={styles.root}>
      <div className={cn(styles.root__grid, {
        [styles['root__grid--with-contents']]: hasContents,
        [styles['root__grid--without-contents']]: !hasContents
      })}>
        <div className={styles.root__top}>
          <h2 className={styles.root__title}>{data?.title}</h2>
          <div className={styles.root__meta}>
            { data?.category?.title && (
              <Chip 
                variant="category" 
                to={categoryUrl}
              >
                {data?.category.title}
              </Chip>
            )}
            {data?.publishedAt && (
              <span className={styles.root__date}>
                {formatDate(data.publishedAt)}
              </span>
            )}
            { data?.viewsCount && (
              <WatchCount countValue={data?.viewsCount || 1} />
            )}
          </div>
          { data.preview?.url && (
            <img
              className={styles.root__img}
              src={data?.preview?.url as string}
              alt=""
              loading="lazy"
              fetchPriority="low"
            />
          )}
          <p className={styles.root__desc}>{data?.lead}</p>
        </div>
        <div className={styles.root__content}>
          <TextMaterialBlock block={data?.content} />
        </div>
        {hasContents && (
          <Contents data={ContentsItems} title={tb('contents')} />
        )}
      </div>
      <Button
        onClick={handleShare}
        className={styles.root__share}
        size="large"
        theme="grey"
      >
        {tb('share.article')} <ReactSVG src="/icons/share.svg" />
      </Button>
      
      {data?.tags && data.tags.length > 0 && (
        <div className={styles.root__tags}>
          <div className={styles['root__tags-list']}>
            {data.tags.map((tag) => (
              <Chip
                key={tag.id}
                variant="tag"
                to={generatePath(URL_BLOG_TAG, { tagSlug: tag.slug })}
              >
                {tag.title}
              </Chip>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
